import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ContactModal from "../components/ContactModal";
import { Button } from "../components/ui/button";
import {
  CheckCircle,
  ArrowRight,
  Shield,
  FileCheck,
  Search,
  Clock,
  Award,
  Users,
  ClipboardCheck,
  SearchCheck,
  ShieldCheck,
} from "lucide-react";
import TestimonialsSlider from "../components/TestimonialsSlider";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import MobileSlider from "../components/MobileSlider";

const TrademarkRegistration = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);

  const openModal = (packageData = null) => {
    setSelectedPackage(packageData);
    setIsModalOpen(true);
  };

  const pricingPlans = [
    {
      name: "Basic",
      price: 45,
      notice:
        "Streamlined trademark support with expert guidance through every stage of the process",
      features: [
        "Case Review",
        "Case Filing",
        "Basic Search",
        "State E-Filing",
        "Trademark Secured",
      ],
    },
    {
      name: "Deluxe",
      price: 149,
      notice:
        "Professional assistance designed to keep your filing organized clear and properly managed",
      features: [
        "Case Review",
        "Case Filing",
        "Basic Trademark Search",
        "State E-Filing",
        "State + Federal E-Filing",
        "Trademark Alert (Basic)",
        "Digital File Access",
        "Case Status Updates",
        "Trademark Secured",
      ],
    },
    {
      name: "Premium",
      price: 395,
      notice:
        "Comprehensive support approach with detailed review strategic guidance and continued assistance",
      popular: true,
      features: [
        "Case Review",
        "Case Filing",
        "Advanced Direct Hit Search",
        "Trademark Secured",
        "Federal & State Comprehensive Search Report",
        "Trademark Alert System",
        "Trademark Monitoring (Basic)",
        "Refusal Risk Check (Basic Level)",
        "Digital File + Documentation",
        "Dedicated Support (Email/Chat)",
        "Trademark Secured",
        "24 Hrs. Expedited Service",
      ],
    },
    {
      name: "Elite",
      price: 595,
      notice:
        "Elevated trademark service experience with dedicated experts focused on accuracy and confidence",
      features: [
        "Case Review",
        "Case Filing",
        "Direct Hit Search",
        "Trademark Alert",
        "Trademark Secured",
        "100% Satisfaction Guarantee",
        "Refusal Risk Assessment",
        "Trademark Monitoring",
        "Complete Documentation",
        "Digital File",
        "Dedicated Case Manager",
        "100% Approval Guarantee",
        "Comprehensive Trademark Search Report (Federal & State)",
        "24 Hrs. Expedited Service",
        "Cease & Desist Letter",
      ],
    },
  ];

  const attorneyPricingPlans = [
    {
      name: "Basic",
      price: 795,
      notice:
        "Dependable trademark assistance designed to simplify the filing journey",
      features: [
        "Attorney case intake review",
        "Trademark information collection",
        "Basic filing readiness check",
        "USPTO application preparation",
        "Standard filing support",
      ],
    },
    {
      name: "Deluxe",
      price: 895,
      notice:
        "Personalized guidance through every stage of the registration process",
      features: [
        "Attorney case intake review",
        "Trademark class guidance",
        "Application drafting by attorney",
        "USPTO filing support",
        "Case status monitoring",
      ],
    },
    {
      name: "Premium",
      price: 1145,
      notice:
        "Structured support with detailed review and professional oversight",
      popular: true,
      features: [
        "Attorney-led strategy review",
        "Comprehensive application drafting",
        "Trademark class and goods review",
        "USPTO filing management",
        "Office correspondence tracking",
        "Priority case updates",
      ],
    },
    {
      name: "Elite",
      price: 1545,
      filingFeeIncluded: true,
      notice:
        "Responsive service with dedicated attention and efficient processing",
      features: [
        "End-to-end attorney-led filing",
        "Advanced trademark filing strategy",
        "Detailed application drafting",
        "USPTO case management",
        "Office action guidance",
        "Ongoing status monitoring",
        "Dedicated attorney support",
      ],
    },
  ];

  const faqs = [
    {
      q: "How long does trademark registration take?",
      a: "The trademark registration process typically takes 8-12 months from filing to registration. We process and file your application within 24 hours.",
    },
    {
      q: "What can be trademarked?",
      a: "You can trademark brand names, logos, slogans, and any unique identifier that distinguishes your goods or services from competitors.",
    },
    {
      q: "Do I need a lawyer to register a trademark?",
      a: "While not required, professional assistance significantly increases your chances of success. Our experienced team handles all complexities.",
    },
    {
      q: "What is the government filing fee?",
      a: "USPTO government filing fees range from $250-$350 per class, which is separate from our service fees.",
    },
    {
      q: "Can my trademark be rejected?",
      a: "Yes, but we handle office actions and work to address any USPTO concerns to maximize your approval chances.",
    },
  ];

  const renderPricingCards = (plans) => (
    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
      {plans.map((plan, idx) => (
        <div
          key={plan.name}
          className={`rounded-2xl p-8 transition-all duration-300 hover:scale-105 flex flex-col h-full animate-scale-in ${plan.popular ? "bg-gradient-to-br from-emerald-600 to-teal-500 text-white shadow-2xl ring-4 ring-emerald-200" : "bg-white shadow-lg hover:shadow-xl border border-emerald-100"}`}
          style={{ animationDelay: `${idx * 0.1}s` }}
        >
          <div className="text-center mb-6">
            <h3
              className={`text-2xl font-bold mb-2 ${plan.popular ? "text-white" : "text-gray-800"}`}
            >
              {plan.name}
            </h3>
            <div className="flex items-baseline justify-center">
              <span
                className={`text-5xl font-bold ${plan.popular ? "text-white" : "text-brand-primary"}`}
              >
                ${plan.price}
              </span>
            </div>
            <p
              className={`text-sm mt-2 ${plan.popular ? "text-emerald-50" : "text-gray-500"}`}
            >
              {plan.filingFeeIncluded
                ? "*(Govt. filing fee included)"
                : "*(Govt. filing fee not included)"}
            </p>
            {plan.notice && (
              <div
                className={`mt-4 rounded-xl p-4 text-sm leading-relaxed ${plan.popular ? "bg-white/15 text-white" : "bg-emerald-50 text-gray-700"}`}
              >
                {plan.notice}
              </div>
            )}
          </div>
          <div className="flex-grow mb-6 overflow-y-auto max-h-64 pr-2 scrollbar-thin">
            <div className="space-y-3">
              {plan.features.map((feature, i) => (
                <div key={i} className="flex items-start space-x-2">
                  <CheckCircle
                    className={`flex-shrink-0 mt-0.5 ${plan.popular ? "text-emerald-100" : "text-brand-primary"}`}
                    size={20}
                  />
                  <span
                    className={`text-sm ${plan.popular ? "text-white" : "text-gray-600"}`}
                  >
                    {feature}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <Button
            onClick={() =>
              openModal({
                name: plan.name,
                price: plan.price,
                tmSignUpType: plan.tmSignUpType,
              })
            }
            className={`w-full mt-auto ${plan.popular ? "bg-white text-brand-primary hover:bg-gray-50 border-2 border-brand-primary" : "bg-brand-gradient text-white hover:opacity-90"}`}
          >
            Choose Plan
          </Button>
        </div>
      ))}
    </div>
  );

  const renderProcessCards = (items) => {
    const processCard = (item) => {
      const Icon = item.icon;
      return (
        <div
          key={item.title}
          className="bg-white rounded-xl border border-gray-200 p-8 text-center shadow-sm hover:shadow-lg transition-all h-full"
        >
          <div className="w-16 h-16 rounded-xl border-2 border-gray-900 flex items-center justify-center mx-auto mb-5">
            <Icon className="text-brand-primary" size={30} />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
          <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
        </div>
      );
    };

    return (
      <div className="max-w-6xl mx-auto mb-12 myprocesscards">
        <div className="grid grid-cols-3 gap-6 max-[576px]:hidden">
          {items.map(processCard)}
        </div>
        <div className="hidden max-[576px]:block px-1 [&_.flex.justify-center.gap-2.mt-6]:mt-5 [&_.flex.justify-center.gap-2.mt-6]:items-center">
          <MobileSlider autoplay={true} showArrows={false} showDots={true}>
            {items.map(processCard)}
          </MobileSlider>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar onOpenModal={() => openModal()} />

      {/* About the Service */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-emerald-50 via-teal-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center animate-fade-in">
            <h1 className="text-5xl font-bold text-gray-800 mb-6">
              Trademark Registration Services
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Protect your brand identity with our comprehensive trademark
              registration services. Starting at just $45, we make brand
              protection accessible, efficient, and hassle-free for businesses
              of all sizes.
            </p>
            <Button
              size="lg"
              onClick={() => openModal()}
              className="bg-brand-gradient text-white hover:opacity-90 group shadow-lg hover:shadow-xl transition-all font-bold text-lg px-8 py-6"
            >
              Get Started Today
              <ArrowRight
                className="ml-2 group-hover:translate-x-1 transition-transform"
                size={24}
              />
            </Button>
          </div>
        </div>
      </section>

      {/* Why You Need It */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 via-teal-50 to-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-gray-800 mb-12 animate-fade-in">
            Why You Need{" "}
            <span className="text-brand-primary">Trademark Registration</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                title: "Legal Protection",
                desc: "Gain exclusive legal rights to your brand identity nationwide and prevent unauthorized use.",
              },
              {
                title: "Brand Recognition",
                desc: "Build customer trust and establish your brand as a credible, professional entity.",
              },
              {
                title: "Competitive Edge",
                desc: "Stand out in the marketplace and prevent competitors from using similar marks.",
              },
              {
                title: "Asset Value",
                desc: "Your trademark becomes a valuable business asset that can appreciate over time.",
              },
              {
                title: "Legal Recourse",
                desc: "Take legal action against infringers and protect your brand investment.",
              },
              {
                title: "Nationwide Rights",
                desc: "Secure protection across all 50 states with potential for global expansion.",
              },
            ].map((item, idx) => (
              <div
                key={idx}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all animate-scale-in"
                style={{ animationDelay: `${idx * 0.1}s` }}
              >
                <h3 className="text-xl font-bold text-gray-800 mb-3">
                  {item.title}
                </h3>
                <p className="text-base text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 animate-fade-in">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              Protecting Brands{" "}
              <span className="bg-brand-gradient bg-clip-text text-transparent">
                with Trusted Trademark Support
              </span>
            </h2>
            <p className="text-base text-gray-600 max-w-5xl mx-auto leading-relaxed">
              Trademark Law Associates provides dependable trademark filing
              services designed to help businesses secure and strengthen their
              brand identity. Our team assists with every stage of the trademark
              process, from application preparation and filing to renewals and
              ongoing monitoring. Whether you are registering a company name,
              logo, phrase, or product identity, we offer tailored support based
              on your business objectives. We proudly assist clients across the
              United States and international markets with protecting valuable
              intellectual property as their brands continue to grow.
            </p>
          </div>

          <Tabs defaultValue="self-filing" className="w-full">
            <TabsList className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-transparent h-auto max-w-4xl mx-auto mb-14 p-0">
              <TabsTrigger
                value="self-filing"
                className="h-14 rounded-lg border border-brand-primary text-lg data-[state=active]:bg-brand-gradient data-[state=active]:text-white"
              >
                Self Filing with Assistance
              </TabsTrigger>
              <TabsTrigger
                value="attorney-led"
                className="h-14 rounded-lg border border-brand-primary text-lg data-[state=active]:bg-brand-gradient data-[state=active]:text-white"
              >
                IP Attorney-Led Filing
              </TabsTrigger>
            </TabsList>

            <TabsContent value="self-filing">
              {renderProcessCards([
                {
                  icon: FileCheck,
                  title: "Application Research & Setup",
                  desc: "Trademark Law Associates reviews your trademark details, confirms the right classification, and prepares the required filing information for USPTO submission.",
                },
                {
                  icon: SearchCheck,
                  title: "Verification & Approval Process",
                  desc: "After ID.me verification, you can review the prepared application and request any edits or updates before final approval.",
                },
                {
                  icon: ShieldCheck,
                  title: "Official Trademark Filing",
                  desc: "Once confirmed, Trademark Law Associates submits your application to the USPTO with accurate and properly organized details.",
                },
              ])}
              <h3 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-10">
                Self-Filing with Expert Assistance and{" "}
                <span className="text-brand-primary">
                  Complete Support Plan for Easy Filing
                </span>
              </h3>
              {renderPricingCards(
                pricingPlans.map((plan) => ({ ...plan, tmSignUpType: 1 })),
              )}
            </TabsContent>

            <TabsContent value="attorney-led">
              {renderProcessCards([
                {
                  icon: ClipboardCheck,
                  title: "Trademark Detail Review",
                  desc: "We collect the essential trademark information, including the name, logo if applicable, and a brief description of your goods or services.",
                },
                {
                  icon: FileCheck,
                  title: "Attorney Review & Filing Preparation",
                  desc: "Our IP attorney reviews your information, identifies the proper trademark class or classes, and prepares a structured USPTO application.",
                },
                {
                  icon: ShieldCheck,
                  title: "Submission & Application Support",
                  desc: "We file the application on your behalf and handle the next steps, including application tracking and USPTO communication.",
                },
              ])}
              <h3 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-10">
                Attorney Led Filing with{" "}
                <span className="text-brand-primary">
                  Complete End-to-End Service Plan
                </span>
              </h3>
              {renderPricingCards(
                attorneyPricingPlans.map((plan) => ({
                  ...plan,
                  tmSignUpType: 2,
                })),
              )}
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 via-teal-50 to-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-gray-800 mb-16 animate-fade-in">
            Why Choose{" "}
            <span className="text-brand-primary">Trademark Law Associates</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {[
              {
                icon: Shield,
                title: "Expert Team",
                desc: "Experienced IP professionals handle your trademark with care",
              },
              {
                icon: Clock,
                title: "24-Hour Filing",
                desc: "Fast processing and filing within one business day",
              },
              {
                icon: Award,
                title: "98% Success Rate",
                desc: "High approval rate with expert application preparation",
              },
              {
                icon: Users,
                title: "24/7 Support",
                desc: "Dedicated customer support at every step",
              },
            ].map((item, idx) => {
              const Icon = item.icon;
              return (
                <div
                  key={idx}
                  className="text-center animate-scale-in"
                  style={{ animationDelay: `${idx * 0.1}s` }}
                >
                  <div className="bg-white w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Icon className="text-brand-primary" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-base text-gray-600">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="animate-fade-in">
              <img
                src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800"
                alt="Benefits"
                className="rounded-2xl shadow-xl hover-zoom"
              />
            </div>
            <div className="animate-slide-up">
              <h2 className="text-4xl font-bold text-gray-800 mb-6">
                Key Benefits of Our Service
              </h2>
              <div className="space-y-4">
                {[
                  "Comprehensive trademark search to avoid conflicts",
                  "Professional application preparation by experts",
                  "USPTO filing and monitoring throughout the process",
                  "Affordable pricing starting at just $45",
                  "Fast turnaround with 24-hour processing available",
                  "100% satisfaction guarantee on all services",
                ].map((benefit, idx) => (
                  <div key={idx} className="flex items-start space-x-3">
                    <CheckCircle
                      className="text-brand-primary flex-shrink-0 mt-1"
                      size={24}
                    />
                    <p className="text-lg text-gray-700">{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      {/* <TestimonialsSlider /> */}

      {/* FAQs */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 via-teal-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-center text-gray-800 mb-12 animate-fade-in">
              Frequently Asked{" "}
              <span className="text-brand-primary">Questions</span>
            </h2>
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, idx) => (
                  <AccordionItem key={idx} value={`item-${idx}`}>
                    <AccordionTrigger className="text-left font-semibold text-gray-800">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-base text-gray-600">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-brand-gradient">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center animate-fade-in">
            <h2 className="text-4xl font-bold text-white mb-6">
              Ready to Protect Your Brand?
            </h2>
            <p className="text-xl text-emerald-50 mb-8">
              Start your trademark registration today and secure your business
              future
            </p>
            <Button
              size="lg"
              onClick={() => navigate("/")}
              className="bg-white text-brand-primary hover:bg-gray-50 shadow-xl border-2 border-brand-primary"
            >
              Get Started Now
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      <ContactModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        selectedPackage={selectedPackage}
      />
    </div>
  );
};

export default TrademarkRegistration;
